import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import LoadingBar from 'react-top-loading-bar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const months = [
  'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December', 'January', 'February', 'March'
];

const TeacherAttendance = () => {
  const drawerWidth = 240;

  // State
  const [yearData, setYearData] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [attendanceData, setAttendanceData] = useState({ attendanceData: [], percentageData: [] });
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Fetch Financial Year Data
  const fetchYearData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/ClassPromotion/GetFinancialYear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        setYearData(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch year data');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setYearData([]);
    }
  };

  // Fetch Attendance Data
  const fetchAttendanceData = async (yearId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const employeeId = sessionStorage.getItem('employeeId');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/EmployeeAttendance/GetEmployeeAttendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ employeeId, yearId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setAttendanceData(responseData);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch attendance data');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setAttendanceData({ attendanceData: [], percentageData: [] });
    }
  };

  // Fetch Year Data on Component Mount
  useEffect(() => {
    fetchYearData();
  }, []);

  // Fetch Attendance Data when Year is Selected
  useEffect(() => {
    if (selectedYear) {
      fetchAttendanceData(selectedYear);
    }
  }, [selectedYear]);

  // Group attendance data by month
  const groupByMonth = (data) => {
    const grouped = {};
    data.forEach((entry) => {
      if (!grouped[entry.month]) {
        grouped[entry.month] = [];
      }
      grouped[entry.month].push(entry);
    });
    return grouped;
  };

  const monthlyData = groupByMonth(attendanceData.attendanceData);

  // Get days in a month
  const getDaysInMonth = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(i);
    }
    return days;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Present':
        return { backgroundColor: '#d4edda', color: '#155724' };
      case 'Absent':
        return { backgroundColor: '#f8d7da', color: '#721c24' };
      case 'Leave':
        return { backgroundColor: '#fff3cd', color: '#856404' };
      case 'Sunday':
        return { backgroundColor: '#e5e2e2', color: '#343a40' };
      default:
        return { backgroundColor: '#f8f9fa', color: '#6c757d' };
    }
  };

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: '64px' },
        p: 3,
        backgroundColor: '#f4f6f9',
        minHeight: '100vh',
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#063552',
          mb: 1,
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 600, letterSpacing: '0.5px' }}>
            My Attendance Calendar
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper
        elevation={3}
        sx={{
          padding: 3,
          width: '100%',
          margin: 'auto',
          backgroundColor: '#fafafa',
          overflow: 'hidden',
        }}
      >
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />

        {/* Year Dropdown */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 1,
            // p: 2,
            // backgroundColor: '#ffffff',
            borderRadius: 2,
            // boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel htmlFor="year">Financial Year</InputLabel>
            <Select
              id="year"
              value={selectedYear}
              label="Financial Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ backgroundColor: '#fff' }}
            >
              {yearData.map((year) => (
                <MenuItem key={year.financialYearID} value={year.financialYearID}>
                  {year.finanacialYear}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Single Table for All Months */}
        {loadingBarProgress > 0 && loadingBarProgress < 100 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={40} thickness={4} />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <TableContainer sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8f9fa', position: 'sticky', left: 0, zIndex: 10 }}></TableCell>
                    {getDaysInMonth().map((day) => (
                      <TableCell key={day} align="center" sx={{ fontWeight: 700, backgroundColor: '#f8f9fa' }}>
                        {day}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {months.map((month) => (
                    <TableRow key={month}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 700, backgroundColor: '#f8f9fa', position: 'sticky', left: 0 }}>
                        {month}
                      </TableCell>
                      {getDaysInMonth().map((day) => {
                        const entry = monthlyData[month]?.find((d) => d.day == day);
                        const isSunday = day % 7 === 0;
                        return (
                          <TableCell key={day} align="center" sx={{ padding: '6px' }}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                minHeight: 40,
                                ...getStatusColor(
                                  isSunday ? 'Sunday' : entry?.attendanceStatus || 'N/A'
                                ),
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {entry?.attendanceStatus || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              borderRadius: 2,
              fontWeight: 500,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default TeacherAttendance;
